import { z } from 'zod';
import type { Role } from '../types/common.js';
import type { AuditAction } from '../types/audit.js';

export interface ToolDefinition<I extends z.ZodTypeAny = z.ZodTypeAny, O extends z.ZodTypeAny = z.ZodTypeAny> {
  name: string;
  /** Shown to the LLM. Must say precisely what the tool does and does not do. */
  description: string;
  input: I;
  output: O;
  /** Roles permitted to invoke. Enforced by the executor, never by the LLM. */
  roles: readonly Role[];
  /** The tool operates on the session's active patient; executor injects patientId and rejects when absent. */
  requiresPatientContext: boolean;
  /** Mutates persistent state. All write tools other than context/preference changes go through drafts + confirmation. */
  write: boolean;
  /** Whether the orchestrator offers this tool to the model. Admin/portal tools are API-only. */
  exposeToLlm: boolean;
  audit?: AuditAction;
}

/** Preserves the literal `name` type so `ToolRegistry` can narrow each tool by name. */
export function defineTool<N extends string, I extends z.ZodTypeAny, O extends z.ZodTypeAny>(def: ToolDefinition<I, O> & { name: N }): ToolDefinition<I, O> & { name: N } {
  return def;
}

export const CLINICAL_STAFF: readonly Role[] = ['PHYSICIAN', 'NURSE'];
export const STAFF: readonly Role[] = ['PHYSICIAN', 'NURSE', 'FRONT_DESK', 'TENANT_ADMIN'];
export const PRESCRIBERS: readonly Role[] = ['PHYSICIAN'];
export const ADMINS: readonly Role[] = ['TENANT_ADMIN', 'SYSTEM_ADMIN'];
export const EVERYONE: readonly Role[] = ['PHYSICIAN', 'NURSE', 'FRONT_DESK', 'TENANT_ADMIN', 'SYSTEM_ADMIN', 'PATIENT'];
